import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import Sidebar, { dektonSamples, caesarstoneSamples, marbleGraniteSamples, porcelainSamples, solidSamples } from './components/Sidebar';
import MainContent from './components/MainContent';
import Header from './components/Header';
import MaterialDetails from './components/MaterialDetails';
import OnboardingTips from './components/OnboardingTips';
import LikedVisualsDrawer from './components/LikedVisualsDrawer';
import CompareModal from './components/CompareModal';
import SplashScreen from './components/SplashScreen';
import { SkeletonGrid, SkeletonScene, SkeletonDetails } from './components/SkeletonLoader';
import { trackEvent, trackSessionSummary, sessionActivity } from './utils/tracking';
import html2canvas from 'html2canvas';
import { FaWhatsapp } from 'react-icons/fa';
import ChatWidget from './components/ChatWidget';
import MobileBottomSheet from './components/MobileBottomSheet';
import ScenePicker from './components/ScenePicker';
import LayoutOverview from './components/LayoutOverview';
import PromoSlideshow from './components/PromoSlideshow';
import './App.css';
import './EngagementEnhancements.css';

// Lazy load components that aren't needed immediately
const SamplesPage = lazy(() => import('./components/SamplesPage'));
const AdminLogin = lazy(() => import('./components/AdminLogin'));
const AdminPanel = lazy(() => import('./components/AdminPanel'));
const MySpacePage = lazy(() => import('./components/MySpacePage'));

const ALL_SAMPLES = [...dektonSamples, ...caesarstoneSamples, ...marbleGraniteSamples, ...porcelainSamples, ...solidSamples];

const BERGEN_SAMPLE = dektonSamples.find(s => s.id === 'Dekton - Bergen') || dektonSamples[0];

const MAX_SAMPLES = 5;

// Loading component for Suspense fallback — uses skeleton
const LoadingFallback = () => (
  <div style={{ display: 'flex', height: '100vh', width: '100%' }}>
    <div style={{ width: '30%' }}>
      <SkeletonGrid count={8} />
    </div>
    <div style={{ flex: 1, position: 'relative', background: '#f5f5f5' }}>
      <SkeletonScene />
    </div>
    <div style={{ width: '320px' }}>
      <SkeletonDetails />
    </div>
  </div>
);


function App() {
  // Desktop: toggles swatch selection mode for bulk cart building
  const handleRequestSampleClick = () => {
    if (likedVisuals.length > 0 && selectedSamples.length === 0) {
      setShowSamplePrompt(true);
    } else {
      setIsSampleSelectionMode(prev => !prev);
    }
  };

  // Mobile bottom bar "REQUEST SAMPLE" / "CHECKOUT"
  // • Cart has items              → open SamplesPage directly
  // • Cart empty + liked visuals  → show prompt (add liked or select manually)
  // • Cart empty + no likes       → open SamplesPage (empty state shown there)
  const handleMobileRequestSample = () => {
    if (selectedSamples.length > 0) {
      setShowSamplesPage(true);
    } else if (likedVisuals.length > 0) {
      setShowSamplePrompt(true);
    } else {
      setShowSamplesPage(true);
    }
  };
  const [chatOpen, setChatOpen] = useState(false);
  const [showSplash, setShowSplash] = useState(true);
  const [activeView, setActiveView] = useState('Kitchens');
  const [activeSubNav, setActiveSubNav] = useState('Solid');
  const [kitchenView, setKitchenView] = useState(1);
  const [vanityView, setVanityView] = useState(1);
  const [wallView, setWallView] = useState(1);
  const [activeOverlay, setActiveOverlay] = useState(null);
  const [layoutPicked, setLayoutPicked] = useState(false);
  const [showLayoutPrompt, setShowLayoutPrompt] = useState(false);
  const [layoutFilter, setLayoutFilter] = useState(null); // null = show all, string = filter to that app
  const [activeSample, setActiveSample] = useState(null); // full sample object for mobile bottom sheet
  const [selectedSamples, setSelectedSamples] = useState([]);
  const [showSamplesPage, setShowSamplesPage] = useState(false);
  const [showMySpacePage, setShowMySpacePage] = useState(false);
  const [isSampleSelectionMode, setIsSampleSelectionMode] = useState(false);
  const [showAdmin] = useState(false);
  const [adminLoggedIn, setAdminLoggedIn] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showSampleLimitPopup, setShowSampleLimitPopup] = useState(false);
  const [cabinetColor, setCabinetColor] = useState(''); // '', 'B', 'BL', 'CR', 'G', '2T'
  const [upperCab, setUpperCab] = useState('G'); // '', 'CR', 'G', 'BL' — maps to scene ID
  const [lowerCab, setLowerCab] = useState('BL'); // '', 'B', 'BL', 'CR', 'G' — maps to scene ID
  const [likedVisuals, setLikedVisuals] = useState(() => {
    try {
      const saved = localStorage.getItem('likedVisuals');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [showLikedPanel, setShowLikedPanel] = useState(false);
  const [showSamplePrompt, setShowSamplePrompt] = useState(false);
  const [isBulkDownloading, setIsBulkDownloading] = useState(false);
  const [bulkDownloadTarget, setBulkDownloadTarget] = useState(null);

  // State for color comparison (max 2 items)
  const [compareItems, setCompareItems] = useState([]);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const compareDidMount = useRef(false);

  // ── Engagement enhancements ──────────────────────────
  const [toasts, setToasts] = useState([]);
  const toastTimersRef = useRef({});
  const [, setDektonViewCount] = useState(0); // value not read — counter only, nudge fires inside setter
  const [showBrandNudge, setShowBrandNudge] = useState(false);
  const [brandNudgeDismissed, setBrandNudgeDismissed] = useState(false);
  const [showRoomNudge, setShowRoomNudge] = useState(false);
  const [showIntroPrompt, setShowIntroPrompt] = useState(false);
  const [showThirdLikePrompt, setShowThirdLikePrompt] = useState(false);
  const [hasShownCompareNudge, setHasShownCompareNudge] = useState(false);
  const [showReturnBanner, setShowReturnBanner] = useState(false);
  const [returnBannerLabel, setReturnBannerLabel] = useState('');

  // Persist liked visuals to localStorage
  useEffect(() => {
    localStorage.setItem('likedVisuals', JSON.stringify(likedVisuals));
  }, [likedVisuals]);

  // Deep-link: ?product=<id> — auto-select sample on load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const productId = params.get('product');
    if (!productId) return;

    const norm = s => s.toLowerCase().replace(/[()]/g, '').replace(/\s+/g, ' ').trim();
    const normalized = norm(productId);

    // Strategy 1: exact normalized match
    let sample = ALL_SAMPLES.find(s => norm(s.id) === normalized);

    // Strategy 2: brand-aware name match (handles SKU in ID, alternate brand prefixes)
    if (!sample) {
      const parts = productId.split(' - ');
      if (parts.length >= 2) {
        const brandRaw = norm(parts[0]);
        const namePart = norm(parts.slice(1).join(' - '));

        const BRAND_MAP = {
          'caesarstone': 'Caesarstone',
          'dekton': 'Dekton',
          'solid': 'Solid',
          'solid by ef': 'Solid',
          'sensa': 'Sensa',
          'granite': 'Sensa',
          'scalea': 'Sensa',
          'porcelain': 'Porcelain',
          'atlas plan': 'Porcelain',
          'atlasplan': 'Porcelain',
        };
        const targetCategory = Object.entries(BRAND_MAP).find(([k]) => brandRaw.startsWith(k))?.[1];

        // Match by label (exact) — safe for all brands
        sample = ALL_SAMPLES.find(s => norm(s.label) === namePart);

        // Match by ID containment — only within known brand to avoid false positives
        if (!sample && targetCategory) {
          const brandSamples = ALL_SAMPLES.filter(s => s.category === targetCategory);
          sample = brandSamples.find(s => norm(s.id).includes(namePart));
        }
      }
    }

    if (sample) {
      // Caesarstone samples use category 'Caesarstone' but sub-nav tab is 'Quartz'
      const CATEGORY_TO_TAB = { 'Caesarstone': 'Quartz' };
      const tab = CATEGORY_TO_TAB[sample.category] || sample.category;

      setActiveOverlay(sample.fullImage);
      setActiveSample(sample);
      setLayoutPicked(true);
      setActiveSubNav(tab);
      setActiveView('Kitchens');
      setKitchenView(1);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  // Auto-open compare modal when 2 items are queued (skip initial mount)
  useEffect(() => {
    if (!compareDidMount.current) {
      compareDidMount.current = true;
      return;
    }
    if (compareItems.length === 2) {
      setShowCompareModal(true);
    }
  }, [compareItems.length]);

  // ── Toast helpers ────────────────────────────────────
  const addToast = (id, msg, cta, ctaFn, duration = 5500) => {
    setToasts(prev => [...prev.filter(t => t.id !== id), { id, msg, cta, ctaFn }]);
    if (toastTimersRef.current[id]) clearTimeout(toastTimersRef.current[id]);
    toastTimersRef.current[id] = setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, duration);
  };
  const dismissToast = (id) => setToasts(prev => prev.filter(t => t.id !== id));

  // ── Compare + third-like nudges driven by like count ─
  useEffect(() => {
    if (likedVisuals.length === 2 && !hasShownCompareNudge) {
      setHasShownCompareNudge(true);
      const last2 = likedVisuals.slice(-2);
      setTimeout(() => {
        addToast('compare-nudge', 'You\'ve liked 2 surfaces — compare side by side?', 'Compare →', () => {
          setCompareItems(last2.map(v => ({
            id: v.id,
            productName: v.label,
            brandName: v.category,
            productImage: v.fullImage,
            image: v.image,
            sceneImage: v.sceneImage,
            overlayImage: v.overlayImage,
          })));
          setShowCompareModal(true);
          dismissToast('compare-nudge');
        });
      }, 1600);
    }
    if (likedVisuals.length === 3 && !showThirdLikePrompt) {
      setShowThirdLikePrompt(true);
    }
  }, [likedVisuals.length]); // eslint-disable-line

  // ── Room timer nudge — 2 min in Kitchens ────────────
  useEffect(() => {
    if (!layoutPicked || activeView !== 'Kitchens' || showRoomNudge) return;
    const t = setTimeout(() => setShowRoomNudge(true), 120000);
    return () => clearTimeout(t);
  }, [layoutPicked, activeView]); // eslint-disable-line

  // Compute the current scene image path (mirrors MainContent logic)
  const getSceneImage = () => {
    switch (activeView) {
      case 'Kitchens':
        switch (kitchenView) {
          case 1:
            if (cabinetColor === '2T') {
              // 2-tone: base is lower color scene, upper overlay is composited in the live view
              // For the saved snapshot, just record the lower base (best static approximation)
              return lowerCab === 'W' ? '/mainpng-white.png' : lowerCab ? `/mainpng2-${lowerCab}.png` : '/mainpng2.png';
            }
            if (cabinetColor === 'W') return '/mainpng-white.png';
            return cabinetColor ? `/mainpng2-${cabinetColor}.png` : '/mainpng2.png';
          case 2: return '/mainpng.png';
          case 3: return '/mainpng3.png';
          default: return '/mainpng.png';
        }
      case 'Vanities':
        switch (vanityView) {
          case 1: return '/mainvanitiespng.png';
          case 2: return '/mainvanitiespng1.png';
          case 3: return '/mainvanitiespng2.png';
          default: return '/mainvanitiespng.png';
        }
      case 'Wall-cladding':
        switch (wallView) {
          case 1: return '/mainwallcladding.png';
          case 2: return '/mainwallcladding1.png';
          case 3: return '/mainwallcladding2.png';
          case 4: return '/mainwallcladding3.png';
          default: return '/mainwallcladding.png';
        }
      default: return '/mainpng.png';
    }
  };

  // Cabinet color ID → display name map
  const CABINET_NAMES = { '': 'Original', 'B': 'Blue', 'BL': 'Black', 'CR': 'Cream', 'G': 'Green', '2T': '2 Tone' };

  // Generate a unique key for a visual based on its full scene context
  const makeVisualKey = (sampleId, view, viewNum, cabColor, upper, lower) => {
    const cab = view === 'Kitchens' && viewNum === 1
      ? (cabColor === '2T' ? `2T-${upper}-${lower}` : cabColor)
      : '';
    return `${sampleId}__${view}__${viewNum}__${cab}`;
  };

  // Current scene view number
  const getCurrentViewNum = () => {
    if (activeView === 'Kitchens') return kitchenView;
    if (activeView === 'Vanities') return vanityView;
    return wallView;
  };

  // Is this slab liked in the current scene?
  const isVisualLiked = (sampleId) => {
    const key = makeVisualKey(sampleId, activeView, getCurrentViewNum(), cabinetColor, upperCab, lowerCab);
    return likedVisuals.some(v => v.visualKey === key);
  };

  // Like/unlike a visual — composite key per scene
  const handleToggleLike = (sample) => {
    const viewNum = getCurrentViewNum();
    const visualKey = makeVisualKey(sample.id, activeView, viewNum, cabinetColor, upperCab, lowerCab);
    const isAlreadyLiked = likedVisuals.some(v => v.visualKey === visualKey);

    setLikedVisuals(prev => {
      const isLiked = prev.some(v => v.visualKey === visualKey);
      if (isLiked) {
        trackEvent('visual_unliked', { sample_id: sample.id, sample_name: sample.label });
        return prev.filter(v => v.visualKey !== visualKey);
      } else {
        trackEvent('visual_liked', { sample_id: sample.id, sample_name: sample.label, category: sample.category });
        const hasCabinet = activeView === 'Kitchens' && kitchenView === 1;
        return [...prev, {
          visualKey,
          id: sample.id,
          label: sample.label,
          category: sample.category,
          image: sample.image,
          fullImage: sample.fullImage,
          sceneImage: getSceneImage(),
          overlayImage: activeOverlay || sample.fullImage,
          application: activeView,
          cabinetColor: hasCabinet ? cabinetColor : null,
          cabinetName: hasCabinet ? (CABINET_NAMES[cabinetColor] || 'Original') : null,
          cabinetSwatch: hasCabinet ? (cabinetColor && cabinetColor !== '2T' ? `/mainpng2-${cabinetColor}-cab.png` : '/mainpng2-cab.png') : null,
          upperCab: hasCabinet && cabinetColor === '2T' ? upperCab : null,
          lowerCab: hasCabinet && cabinetColor === '2T' ? lowerCab : null
        }];
      }
    });

    // Fire like toast (optimistic — skip if unliking)
    if (!isAlreadyLiked) {
      const waText = `Hi Elegant Fittings! I love ${sample.label} and would love a free sample. Can you help?`;
      addToast(
        'like',
        `❤ ${sample.label} saved`,
        'Request sample →',
        () => window.open(`https://wa.me/254745969500?text=${encodeURIComponent(waText)}`, '_blank')
      );
    }
  };

  // Unlike from drawer — uses stored visualKey since active scene may differ
  const handleUnlikeByKey = (visual) => {
    trackEvent('visual_unliked', { sample_id: visual.id, sample_name: visual.label });
    setLikedVisuals(prev => prev.filter(v => v.visualKey !== visual.visualKey));
  };

  const onCheckoutFavorites = () => {
    if (likedVisuals.length === 0) return;

    setSelectedSamples(prev => {
      let newSamples = [...prev];
      likedVisuals.forEach(visual => {
        if (newSamples.length < MAX_SAMPLES && !newSamples.some(s => s.id === visual.id)) {
          newSamples.push({
            id: visual.id,
            name: visual.label,
            category: visual.category,
            thumbnail: visual.image,
            fullImage: visual.fullImage
          });
        }
      });
      return newSamples;
    });

    setIsSampleSelectionMode(false);
    setShowSamplesPage(true);
    trackEvent('checkout_favorites_clicked', { count: likedVisuals.length });
  };

  const onOpenSamplesPage = () => {
    setIsSampleSelectionMode(false);
    setShowSamplesPage(true);
  };


  const handleClearAllLikes = () => {
    trackEvent('all_likes_cleared', { count: likedVisuals.length });
    setLikedVisuals([]);
  };

  // Color Comparison Handlers
  const handleToggleCompare = (sample) => {
    setCompareItems(prev => {
      const isInCompare = prev.some(item => item.id === sample.id);
      if (isInCompare) {
        trackEvent('compare_item_removed', { sample_id: sample.id, sample_name: sample.label });
        return prev.filter(item => item.id !== sample.id);
      }

      if (prev.length >= 2) {
        // Prevent adding more than 2 items
        return prev;
      }

      trackEvent('compare_item_added', { sample_id: sample.id, sample_name: sample.label });
      return [...prev, {
        id: sample.id,
        productName: sample.label,
        brandName: sample.category,
        productImage: sample.fullImage, // The high-res overlay image
        image: sample.image,           // The thumbnail image
        sceneImage: getSceneImage(),
        overlayImage: activeOverlay,
        addedAt: new Date().toISOString()
      }];
    });
  };

  const isItemInCompare = (sampleId) => compareItems.some(item => item.id === sampleId);
  const handleClearCompare = () => setCompareItems([]);

  // Track window resize for mobile detection, session start and session summary on exit
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);

    // Track session start
    trackEvent('session_start', {
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      view_port: `${window.innerWidth}x${window.innerHeight}`
    });

    // Fire session summary when user closes tab / navigates away
    const handlePageHide = () => trackSessionSummary('pagehide');
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') trackSessionSummary('visibility_hidden');
    };
    window.addEventListener('pagehide', handlePageHide);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('pagehide', handlePageHide);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  console.log('App rendering, activeOverlay:', activeOverlay); // Debug log
  console.log('Kitchen View:', kitchenView);
  console.log('Vanity View:', vanityView);
  console.log('Wall View:', wallView);

  // Make sure these numbers match your total views
  const totalKitchens = 3;  // Should be 3 to include mainpng3.png
  const totalVanities = 3;  // Changed from 2 to 3
  const totalWalls = 4;     // Should be 4 to include all wall cladding images

  // Make sure your handleNext function can handle this:
  const handleNext = () => {
    let nextVal;
    if (activeView === 'Kitchens') {
      nextVal = kitchenView === totalKitchens ? 1 : kitchenView + 1;
      setKitchenView(nextVal);
      setCabinetColor('');
    } else if (activeView === 'Vanities') {
      nextVal = vanityView === totalVanities ? 1 : vanityView + 1;
      setVanityView(nextVal);
    } else if (activeView === 'Wall-cladding') {
      nextVal = wallView === totalWalls ? 1 : wallView + 1;
      setWallView(nextVal);
    }

    trackEvent('navigation_click', {
      direction: 'next',
      application: activeView,
      new_index: nextVal,
      total: getTotalViews()
    });
  };

  // And your handlePrevious function:
  const handlePrevious = () => {
    let nextVal;
    if (activeView === 'Kitchens') {
      nextVal = kitchenView === 1 ? totalKitchens : kitchenView - 1;
      setKitchenView(nextVal);
      setCabinetColor('');
    } else if (activeView === 'Vanities') {
      nextVal = vanityView === 1 ? totalVanities : vanityView - 1;
      setVanityView(nextVal);
    } else if (activeView === 'Wall-cladding') {
      nextVal = wallView === 1 ? totalWalls : wallView - 1;
      setWallView(nextVal);
    }

    trackEvent('navigation_click', {
      direction: 'previous',
      application: activeView,
      new_index: nextVal,
      total: getTotalViews()
    });
  };

  const handleSelectScene = (num) => {
    if (activeView === 'Kitchens') { setKitchenView(num); setCabinetColor(''); }
    else if (activeView === 'Vanities') setVanityView(num);
    else if (activeView === 'Wall-cladding') setWallView(num);
    trackEvent('scene_selected', { application: activeView, scene_num: num });
  };

  const handleSelectLayout = (appView, sceneNum) => {
    setActiveView(appView);
    setCabinetColor('');
    if (appView === 'Kitchens') setKitchenView(sceneNum);
    else if (appView === 'Vanities') setVanityView(sceneNum);
    else if (appView === 'Wall-cladding') setWallView(sceneNum);
    setLayoutPicked(true);
    setShowLayoutPrompt(false);
    trackEvent('layout_selected', { application: appView, scene_num: sceneNum });
  };

  const onSampleClick = (sampleOrPath) => {
    // If no layout has been picked yet, prompt user to select one first
    if (!layoutPicked) {
      setShowLayoutPrompt(true);
      setTimeout(() => setShowLayoutPrompt(false), 2000);
      return;
    }

    // Accept either a full sample object (from Sidebar) or a path string (from LikedVisualsDrawer)
    const overlayPath = typeof sampleOrPath === 'string' ? sampleOrPath : sampleOrPath.fullImage;
    const sampleObj = typeof sampleOrPath === 'object' ? sampleOrPath : null;

    setActiveOverlay(overlayPath);
    if (sampleObj) {
      setActiveSample(sampleObj);
      const params = new URLSearchParams(window.location.search);
      params.set('product', sampleObj.id);
      window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`);

      // Track Dekton views for brand discovery nudge
      if (sampleObj.category === 'Dekton') {
        setDektonViewCount(prev => {
          const next = prev + 1;
          if (next >= 3 && !brandNudgeDismissed) setShowBrandNudge(true);
          return next;
        });
      }

      // Dismiss intro prompt on first material interaction
      if (showIntroPrompt) setShowIntroPrompt(false);

      // Persist last-viewed material for return-visitor detection
      try {
        localStorage.setItem('ef_lastSample', JSON.stringify({ id: sampleObj.id, label: sampleObj.label, fullImage: sampleObj.fullImage, image: sampleObj.image, category: sampleObj.category }));
        localStorage.setItem('ef_lastView', activeView);
        localStorage.setItem('ef_lastCab', cabinetColor);
      } catch (_) {}
    }

    // Extract material name from path for tracking
    const materialId = overlayPath.split('/').pop().split('.')[0];
    const cleanName = materialId.replace(/-/g, ' ').replace(/\.[^/.]+$/, "");

    trackEvent('material_view', {
      material_id: materialId,
      material_name: cleanName,
      application: activeView,
      application_index: getCurrentView(),
      brand: activeSubNav
    });
  };

  // Sample selection functions
  const handleToggleSampleSelection = (sample) => {
    setSelectedSamples(prev => {
      const isSelected = prev.some(s => s.id === sample.id);
      if (isSelected) {
        trackEvent('sample_removed', {
          sample_id: sample.id,
          sample_name: sample.label
        });
        return prev.filter(s => s.id !== sample.id);
      } else {
        // Check if limit reached
        if (prev.length >= MAX_SAMPLES) {
          setShowSampleLimitPopup(true);
          trackEvent('limit_reached', { current_count: prev.length });
          return prev; // Don't add
        }

        trackEvent('sample_added', {
          sample_id: sample.id,
          sample_name: sample.label,
          category: sample.category
        });

        return [...prev, {
          id: sample.id,
          name: sample.label,
          category: sample.category,
          thumbnail: sample.image,
          fullImage: sample.fullImage
        }];
      }
    });
  };

  const handleRemoveSample = (sampleId) => {
    setSelectedSamples(prev => prev.filter(s => s.id !== sampleId));
  };

  const handleRequestAllSamples = (samples) => {
    const sampleNames = selectedSamples.map(s => s.name).join(', ');
    trackEvent('checkout_started', {
      total_samples: selectedSamples.length,
      sample_names: sampleNames
    });
    setShowSamplesPage(true);
  };

  const handleClearCart = () => {
    setSelectedSamples([]);
  };

  const toggleSampleSelectionMode = () => {
    setIsSampleSelectionMode(prev => !prev);
  };

  const handleBackToVisualizer = () => {
    setShowSamplesPage(false);
  };

  const handleClearCartAndBack = () => {
    setSelectedSamples([]);
    setShowSamplesPage(false);
  };

  const handleDownload = async () => {
    const captureArea = document.querySelector('#download-capture-area');
    if (captureArea) {
      try {
        const canvas = await html2canvas(captureArea, {
          useCORS: true,
          scale: 2,
          backgroundColor: '#ffffff',
          logging: false,
          windowWidth: 1400,
          windowHeight: 1200,
          onclone: (clonedDoc) => {
            // Force virtual viewport dimensions in the clone
            clonedDoc.documentElement.style.width = '1400px';
            clonedDoc.documentElement.style.height = '1200px';
            clonedDoc.body.style.width = '1400px';
            clonedDoc.body.style.height = '1200px';
            clonedDoc.body.style.overflow = 'auto';

            const clonedArea = clonedDoc.querySelector('#download-capture-area');
            if (clonedArea) {
              clonedArea.style.top = '0';
              clonedArea.style.left = '0';
              clonedArea.style.position = 'relative';
              clonedArea.style.display = 'flex';
              clonedArea.style.visibility = 'visible';
              clonedArea.style.opacity = '1';
            }
          }
        });

        const visualizationType = activeView.toLowerCase().replace('-', '-');
        let materialName = 'default-material';
        if (activeOverlay) {
          const pathParts = activeOverlay.split('/');
          const filenamePart = pathParts[pathParts.length - 1];
          materialName = filenamePart.replace(/\.[^/.]+$/, '');
        }
        const sanitizedMaterialName = materialName.replace(/[^a-zA-Z0-9]/g, '-').toUpperCase();
        const filename = `EF-SPEC-${visualizationType}-${sanitizedMaterialName}.png`;

        const link = document.createElement('a');
        link.download = filename;
        link.href = canvas.toDataURL('image/png', 1.0);
        link.click();

        const dlLabel = activeSample?.label || sanitizedMaterialName;
        const dlWaText = `Hi! I've downloaded a spec for ${dlLabel} and I'm planning a project. Can we discuss?`;
        addToast(
          'download',
          `${dlLabel} downloaded`,
          'Talk to our team →',
          () => window.open(`https://wa.me/254745969500?text=${encodeURIComponent(dlWaText)}`, '_blank')
        );

        trackEvent('visual_downloaded', {
          filename,
          sample_id:    activeSample?.id || sanitizedMaterialName,
          sample_name:  activeSample?.label || sanitizedMaterialName,
          category:     activeSample?.category || activeSubNav,
          application:  activeView,
          cabinet_name: cabinetColor === '2T' ? '2 Tone' : (cabinetColor || 'Original'),
          upper_cab:    cabinetColor === '2T' ? upperCab : null,
          lower_cab:    cabinetColor === '2T' ? lowerCab : null,
          download_type: 'single',
        });
        sessionActivity.downloadsCount++;
      } catch (error) {
        console.error('Error generating download:', error);
      }
    }
  };

  const handleBulkDownload = async () => {
    if (likedVisuals.length === 0 || isBulkDownloading) return;

    setIsBulkDownloading(true);
    trackEvent('bulk_download_started', { count: likedVisuals.length });

    try {
      for (const visual of likedVisuals) {
        setBulkDownloadTarget(visual);
        // Wait for state to update and images to potentially load
        await new Promise(resolve => setTimeout(resolve, 800));

        const captureArea = document.querySelector('#download-capture-area');
        if (captureArea) {
          const canvas = await html2canvas(captureArea, {
            useCORS: true,
            scale: 2,
            backgroundColor: '#ffffff',
            logging: false,
            windowWidth: 1400,
            windowHeight: 1200,
            onclone: (clonedDoc) => {
              clonedDoc.documentElement.style.width = '1400px';
              clonedDoc.documentElement.style.height = '1200px';
              clonedDoc.body.style.width = '1400px';
              clonedDoc.body.style.height = '1200px';
              const clonedArea = clonedDoc.querySelector('#download-capture-area');
              if (clonedArea) {
                clonedArea.style.top = '0';
                clonedArea.style.left = '0';
                clonedArea.style.position = 'relative';
                clonedArea.style.display = 'flex';
                clonedArea.style.visibility = 'visible';
                clonedArea.style.opacity = '1';
              }
            }
          });

          const filename = `EF-SPEC-SAVED-${visual.label.replace(/[^a-zA-Z0-9]/g, '-').toUpperCase()}.png`;
          const link = document.createElement('a');
          link.download = filename;
          link.href = canvas.toDataURL('image/png', 1.0);
          link.click();

          // Track each visual individually with full context
          trackEvent('visual_downloaded', {
            sample_id:    visual.id,
            sample_name:  visual.label,
            category:     visual.category,
            application:  visual.application,
            cabinet_name: visual.cabinetName || 'Original',
            upper_cab:    visual.upperCab || null,
            lower_cab:    visual.lowerCab || null,
            download_type: 'bulk',
          });
        }
      }
      sessionActivity.downloadsCount += likedVisuals.length;
    } catch (error) {
      console.error('Error during bulk download:', error);
    } finally {
      setBulkDownloadTarget(null);
      setIsBulkDownloading(false);
    }
  };

  // Get current view count based on active view
  const getCurrentView = () => {
    switch (activeView) {
      case 'Kitchens':
        return kitchenView;
      case 'Vanities':
        return vanityView;
      case 'Wall-cladding':
        return wallView;
      default:
        return 1;
    }
  };

  const getTotalViews = () => {
    switch (activeView) {
      case 'Kitchens':
        return totalKitchens;
      case 'Vanities':
        return totalVanities;
      case 'Wall-cladding':
        return totalWalls;
      default:
        return 1;
    }
  };

  // Splash complete — auto-load Bergen (or restored session) and set up initial scene
  const handleSplashComplete = () => {
    // Deep link already resolved (activeSample set by useEffect) — don't override
    if (activeSample) { setShowSplash(false); return; }

    try {
      const savedRaw = localStorage.getItem('ef_lastSample');
      const savedCab = localStorage.getItem('ef_lastCab') || '2T';
      const hasVisited = localStorage.getItem('ef_hasVisited');
      const sample = savedRaw ? JSON.parse(savedRaw) : BERGEN_SAMPLE;

      // Return visitor with prior likes — show welcome-back banner
      if (hasVisited && savedRaw && likedVisuals.length > 0) {
        setReturnBannerLabel(sample.label || 'your last surface');
        setShowReturnBanner(true);
      }

      // First-time visitor — show contextual intro
      if (!hasVisited) setShowIntroPrompt(true);

      localStorage.setItem('ef_hasVisited', '1');
      setActiveSample(sample);
      setActiveOverlay(sample.fullImage);
      setActiveView('Kitchens');
      setKitchenView(1);
      setCabinetColor(savedCab);
      setLayoutPicked(true);
      if (!savedRaw) setActiveSubNav('Dekton');
    } catch (_) {
      setActiveSample(BERGEN_SAMPLE);
      setActiveOverlay(BERGEN_SAMPLE.fullImage);
      setActiveView('Kitchens');
      setKitchenView(1);
      setCabinetColor('2T');
      setLayoutPicked(true);
      setActiveSubNav('Dekton');
      setShowIntroPrompt(true);
    }
    setShowSplash(false);
  };

  // Splash screen
  if (showSplash) {
    return <SplashScreen onComplete={handleSplashComplete} />;
  }

  // Admin navigation logic
  if (showAdmin) {
    if (!adminLoggedIn) {
      return (
        <Suspense fallback={<LoadingFallback />}>
          <AdminLogin onLogin={() => setAdminLoggedIn(true)} />
        </Suspense>
      );
    }
    return (
      <Suspense fallback={<LoadingFallback />}>
        <AdminPanel />
      </Suspense>
    );
  }

  if (showMySpacePage) {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <MySpacePage onBack={() => setShowMySpacePage(false)} />
      </Suspense>
    );
  }

  // If showing samples page, render that instead of main app
  if (showSamplesPage) {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <SamplesPage
          selectedSamples={selectedSamples}
          onBack={handleBackToVisualizer}
          onClearCart={handleClearCartAndBack}
        />
      </Suspense>
    );
  }

  return (
    <div className="container">
      {/* Onboarding Tips */}
      {showOnboarding && <OnboardingTips onComplete={() => setShowOnboarding(false)} />}

      <Header
        activeView={activeView}
        setActiveView={(view) => {
          setActiveView(view);
          setCabinetColor('');
          setLayoutPicked(false);
          setActiveOverlay(null);
          setActiveSample(null);
          setLayoutFilter(view);
          setShowIntroPrompt(false);
          setShowReturnBanner(false);
        }}
        currentView={getCurrentView()}
        totalViews={getTotalViews()}
        handleNext={handleNext}
        handlePrevious={handlePrevious}
        selectedSamples={selectedSamples}
        onCheckout={() => setShowSamplesPage(true)}
        activeSubNav={activeSubNav}
        activeOverlay={activeOverlay}
        handleRequestSampleClick={handleRequestSampleClick}
        isSampleSelectionMode={isSampleSelectionMode}
        onToggleLikedPanel={() => setShowLikedPanel(prev => !prev)}
        likedCount={likedVisuals.length}
        compareCount={compareItems.length}
        onToggleComparePanel={() => setShowCompareModal(true)}
        onBulkDownload={handleBulkDownload}
        onDownload={handleDownload}
        layoutPicked={layoutPicked}
        onMySpace={() => setShowMySpacePage(true)}
        onChangeRoom={() => {
          setLayoutPicked(false);
          setActiveOverlay(null);
          setActiveSample(null);
          setShowIntroPrompt(false);
          setShowReturnBanner(false);
        }}
      />
      <div className="main-layout">
        <Sidebar
          activeView={activeView}
          setActiveView={setActiveView}
          activeSubNav={activeSubNav}
          setActiveSubNav={setActiveSubNav}
          kitchenView={kitchenView}
          vanityView={vanityView}
          wallView={wallView}
          totalKitchens={totalKitchens}
          totalVanities={totalVanities}
          totalWalls={totalWalls}
          handleNext={handleNext}
          handlePrevious={handlePrevious}
          setActiveOverlay={setActiveOverlay}
          onSampleClick={onSampleClick}
          selectedSamples={selectedSamples}
          onToggleSampleSelection={handleToggleSampleSelection}
          isSampleSelectionMode={isSampleSelectionMode}
          onToggleSampleSelectionMode={toggleSampleSelectionMode}
          activeOverlay={activeOverlay}
          likedVisuals={likedVisuals}
          onToggleLike={handleToggleLike}
          isVisualLiked={isVisualLiked}
          onToggleCompare={handleToggleCompare}
          isItemInCompare={isItemInCompare}
          activeSample={activeSample}
          layoutPicked={layoutPicked}
          showBrandNudge={showBrandNudge}
          onBrandNudgeDismiss={() => { setShowBrandNudge(false); setBrandNudgeDismissed(true); }}
          onSwitchBrand={(brand) => { setActiveSubNav(brand); setShowBrandNudge(false); setBrandNudgeDismissed(true); }}
        />
        <div className="mobile-scene-container">
          {/* Intro prompt — first-visit contextual hint */}
          {showIntroPrompt && layoutPicked && (
            <div className="intro-prompt">
              <span>Viewing <strong>{activeSample?.label || 'Dekton Bergen'}</strong> in a 2-tone kitchen — tap any surface to swap it.</span>
              <button className="intro-prompt-close" onClick={() => setShowIntroPrompt(false)}>✕</button>
            </div>
          )}

          {/* Return visitor banner */}
          {showReturnBanner && layoutPicked && (
            <div className="return-banner">
              <span className="return-banner-text">Welcome back — you were looking at <span className="return-banner-label">{returnBannerLabel}</span></span>
              <button className="return-banner-close" onClick={() => setShowReturnBanner(false)}>✕</button>
            </div>
          )}

          {!layoutPicked ? (
            /* ── Default: layout overview ── */
            <LayoutOverview
              onSelect={handleSelectLayout}
              showPrompt={showLayoutPrompt}
              filterApp={layoutFilter}
            />
          ) : (
            /* ── Scene mode: active after layout picked ── */
            <>
              {/* Room nudge — after 2 min in Kitchens */}
              {showRoomNudge && activeView === 'Kitchens' && (
                <div className="room-nudge">
                  <span>Also picture {activeSample?.label || 'this surface'} in your bathroom →</span>
                  <button className="room-nudge-btn" onClick={() => {
                    setActiveView('Vanities');
                    setVanityView(1);
                    setLayoutPicked(true);
                    setShowRoomNudge(false);
                  }}>Try Vanities</button>
                  <button className="room-nudge-close" onClick={() => setShowRoomNudge(false)}>✕</button>
                </div>
              )}

              <MainContent
                activeOverlay={activeOverlay}
                activeView={activeView}
                kitchenView={kitchenView}
                vanityView={vanityView}
                wallView={wallView}
                cabinetColor={cabinetColor}
                upperCab={upperCab}
                lowerCab={lowerCab}
                sceneLikeBtn={activeSample ? (
                  <button
                    className={`scene-like-btn${isVisualLiked(activeSample.id) ? ' liked' : ''}`}
                    onClick={() => handleToggleLike(activeSample)}
                    title={isVisualLiked(activeSample.id) ? 'Unlike this visual' : 'Like this visual'}
                  >
                    {isVisualLiked(activeSample.id) ? '♥' : '♡'}
                  </button>
                ) : null}
              />
              {/* Scene nav pill — mobile only */}
              {isMobile && (
                <div className="mobile-scene-nav">
                  <button className="mobile-scene-nav-arrow" onClick={handlePrevious} aria-label="Previous scene">&#8249;</button>
                  <span className="mobile-scene-nav-counter">{getCurrentView()} / {getTotalViews()}</span>
                  <button className="mobile-scene-nav-arrow" onClick={handleNext} aria-label="Next scene">&#8250;</button>
                </div>
              )}
              {/* Scene thumbnail picker — mobile only */}
              {isMobile && (
                <ScenePicker
                  activeView={activeView}
                  currentViewNum={getCurrentViewNum()}
                  onSelect={handleSelectScene}
                />
              )}
            </>
          )}
        </div>
        {!layoutPicked && <PromoSlideshow />}
        {layoutPicked && <MaterialDetails
          activeOverlay={activeOverlay}
          activeSubNav={activeSubNav}
          selectedSamples={selectedSamples}
          onRemoveSample={handleRemoveSample}
          onRequestAllSamples={handleRequestAllSamples}
          onClearCart={handleClearCart}
          isSampleSelectionMode={isSampleSelectionMode}
          onToggleSampleSelectionMode={handleRequestSampleClick}
          kitchenView={kitchenView}
          activeView={activeView}
          cabinetColor={cabinetColor}
          setCabinetColor={setCabinetColor}
          upperCab={upperCab}
          setUpperCab={setUpperCab}
          lowerCab={lowerCab}
          setLowerCab={setLowerCab}
          onSampleClick={onSampleClick}
          onToggleCompare={handleToggleCompare}
          activeSample={activeSample}
        />}

        {/* Inline material detail panel — mobile only, only when a scene is active */}
        {isMobile && layoutPicked && (
          <div className="mobile-detail-wrap">
          <div className="mobile-inline-detail">
            <MobileBottomSheet
              inline={true}
              activeSample={activeSample}
              activeView={activeView}
              kitchenView={kitchenView}
              cabinetColor={cabinetColor}
              setCabinetColor={setCabinetColor}
              upperCab={upperCab}
              setUpperCab={setUpperCab}
              lowerCab={lowerCab}
              setLowerCab={setLowerCab}
              onToggleLike={handleToggleLike}
              isVisualLiked={isVisualLiked}
              onToggleCompare={handleToggleCompare}
              isItemInCompare={isItemInCompare}
              compareCount={compareItems.length}
              onToggleSampleSelection={handleToggleSampleSelection}
              selectedSamples={selectedSamples}
              onSampleClick={onSampleClick}
              activeOverlay={activeOverlay}
              sceneImage={getSceneImage()}
            />
          </div>
          </div>
        )}
      </div>

      <LikedVisualsDrawer
        isOpen={showLikedPanel}
        onClose={() => setShowLikedPanel(false)}
        likedVisuals={likedVisuals}
        onUnlike={handleUnlikeByKey}
        onClearAll={handleClearAllLikes}
        onVisualClick={onSampleClick}
        onBulkDownload={handleBulkDownload}
        isBulkDownloading={isBulkDownloading}
      />

      <CompareModal
        isOpen={showCompareModal}
        onClose={() => setShowCompareModal(false)}
        compareItems={compareItems}
        onRemove={(id) => setCompareItems(prev => prev.filter(item => item.id !== id))}
        onClearAll={handleClearCompare}
        onToggleLike={handleToggleLike}
        isVisualLiked={isVisualLiked}
        onAddToCart={() => {
          setShowCompareModal(false);
          setShowSamplesPage(true);
        }}
      />

      {/* Mobile Fixed Bottom CTA — always 3 items; count badge on REQUEST SAMPLE when cart has items */}
      {isMobile && (
        <div className="mobile-bottom-cta">
          <button
            className={`request-sample-toggle-btn${selectedSamples.length > 0 ? ' has-items' : ''}`}
            onClick={handleMobileRequestSample}
          >
            {selectedSamples.length > 0
              ? <>CHECKOUT <span className="mobile-cart-badge">{selectedSamples.length}</span></>
              : 'REQUEST SAMPLE'
            }
          </button>
          {activeSample && (() => {
            const productId = activeSample.id;
            const shareUrl = `${window.location.origin}/?product=${encodeURIComponent(productId)}`;
            const waText = `Hello Elegant Fittings! I'm interested in purchasing:\n\nProduct: ${activeSample.label || activeSample.id}\nPage: ${shareUrl}\n\nPlease advise on availability and pricing.`;
            const handleOrder = () => {
              fetch('https://elegantfittings.app.n8n.cloud/webhook/ef-whatsapp-order', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  product: activeSample.label || activeSample.id,
                  productId,
                  shareUrl,
                  brand: activeSample.category || '',
                  timestamp: new Date().toISOString(),
                  source: 'visualizer-mobile-bottom-order',
                }),
              }).catch(() => {});
            };
            return (
              <a
                className="mobile-order-btn"
                href={`https://wa.me/254745969500?text=${encodeURIComponent(waText)}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleOrder}
              >
                <FaWhatsapp size={18} />
                <span>Order</span>
              </a>
            );
          })()}
          {activeSample && (
            <button
              className={`mobile-add-btn${selectedSamples.some(s => s.id === activeSample.id) ? ' in-cart' : ''}`}
              onClick={() => handleToggleSampleSelection(activeSample)}
            >
              {selectedSamples.some(s => s.id === activeSample.id) ? '✓' : '+ Add'}
            </button>
          )}
          <button
            className="mobile-chat-btn"
            onClick={() => setChatOpen(prev => !prev)}
            title="Chat with us"
          >
            <FaWhatsapp size={24} />
          </button>
        </div>
      )}

      {/* Sample Request Prompt — shown when user has liked visuals but empty cart */}
      {showSamplePrompt && (
        <div className="sample-limit-overlay" onClick={() => setShowSamplePrompt(false)}>
          <div className="sample-limit-popup" onClick={e => e.stopPropagation()}>
            <h3>Request a Sample</h3>
            <p>You have <strong>{likedVisuals.length} liked visual{likedVisuals.length !== 1 ? 's' : ''}</strong>. How would you like to proceed?</p>
            <div className="sample-prompt-actions">
              <button
                className="sample-prompt-btn primary"
                onClick={() => {
                  setShowSamplePrompt(false);
                  onCheckoutFavorites();
                }}
              >
                Add liked slabs to cart ({[...new Set(likedVisuals.map(v => v.id))].length} slab{[...new Set(likedVisuals.map(v => v.id))].length !== 1 ? 's' : ''})
              </button>
              <button
                className="sample-prompt-btn secondary"
                onClick={() => {
                  setShowSamplePrompt(false);
                  setIsSampleSelectionMode(true);
                }}
              >
                Select samples manually
              </button>
            </div>
            <button className="sample-limit-close" onClick={() => setShowSamplePrompt(false)}>✕</button>
          </div>
        </div>
      )}

      {/* Sample Limit Popup */}
      {showSampleLimitPopup && (
        <div className="sample-limit-overlay" onClick={() => setShowSampleLimitPopup(false)}>
          <div className="sample-limit-popup" onClick={(e) => e.stopPropagation()}>
            <div className="popup-icon">⚠️</div>
            <h3>Sample Limit Reached</h3>
            <p>You can only request up to <strong>5 samples</strong> at a time.</p>
            <p className="popup-subtext">Need more? Book a showroom visit to explore our full collection in person.</p>
            <div className="popup-actions">
              <button
                className="popup-btn-secondary"
                onClick={() => setShowSampleLimitPopup(false)}
              >
                Got it
              </button>
              <a
                href="https://elegantfittings.co.ke/contact"
                target="_blank"
                rel="noopener noreferrer"
                className="popup-btn-primary"
              >
                Book Showroom Visit
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Third-like prompt modal */}
      {showThirdLikePrompt && (
        <div className="sample-limit-overlay" onClick={() => setShowThirdLikePrompt(false)}>
          <div className="sample-limit-popup" onClick={e => e.stopPropagation()}>
            <div className="third-like-emoji">❤️❤️❤️</div>
            <h3>You've saved 3 surfaces!</h3>
            <p>Want to experience them in person? We deliver free samples anywhere in Kenya — no charge.</p>
            <div className="sample-prompt-actions">
              <button
                className="sample-prompt-btn primary"
                onClick={() => { setShowThirdLikePrompt(false); onCheckoutFavorites(); }}
              >
                Request free samples →
              </button>
              <a
                href={`https://wa.me/254745969500?text=${encodeURIComponent("Hi! I've saved 3 surfaces on your visualizer and would love to see them in person.")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="sample-prompt-btn secondary"
                onClick={() => setShowThirdLikePrompt(false)}
              >
                Chat on WhatsApp
              </a>
            </div>
            <button className="sample-limit-close" onClick={() => setShowThirdLikePrompt(false)}>✕</button>
          </div>
        </div>
      )}

      {/* Toast notification stack */}
      <div className="toast-stack">
        {toasts.map(t => (
          <div key={t.id} className="toast-item">
            <span className="toast-msg">{t.msg}</span>
            {t.cta && <button className="toast-cta" onClick={t.ctaFn}>{t.cta}</button>}
            <button className="toast-close" onClick={() => dismissToast(t.id)}>✕</button>
          </div>
        ))}
      </div>

      {/* Hidden Download Capture Area - Styled for professional output */}
      <div id="download-capture-area" className={`download-capture-area ${isBulkDownloading ? 'bulk-active' : ''}`}>
        <div className="download-layout-container">
          <div className="download-visualization">
            <MainContent
              activeOverlay={bulkDownloadTarget ? bulkDownloadTarget.overlayImage : activeOverlay}
              activeView={bulkDownloadTarget ? bulkDownloadTarget.application : activeView}
              kitchenView={bulkDownloadTarget ? (bulkDownloadTarget.application === 'Kitchens' ? 1 : kitchenView) : kitchenView}
              vanityView={bulkDownloadTarget ? (bulkDownloadTarget.application === 'Vanities' ? 1 : vanityView) : vanityView}
              wallView={bulkDownloadTarget ? (bulkDownloadTarget.application === 'Wall-cladding' ? 1 : wallView) : wallView}
              cabinetColor={bulkDownloadTarget ? bulkDownloadTarget.cabinetColor : cabinetColor}
              upperCab={bulkDownloadTarget ? (bulkDownloadTarget.upperCab || upperCab) : upperCab}
              lowerCab={bulkDownloadTarget ? (bulkDownloadTarget.lowerCab || lowerCab) : lowerCab}
            />
          </div>
          <div className="download-details">
            <MaterialDetails
              activeOverlay={bulkDownloadTarget ? bulkDownloadTarget.overlayImage : activeOverlay}
              activeSubNav={bulkDownloadTarget ? bulkDownloadTarget.category : activeSubNav}
              kitchenView={bulkDownloadTarget ? (bulkDownloadTarget.application === 'Kitchens' ? 1 : kitchenView) : kitchenView}
              activeView={bulkDownloadTarget ? bulkDownloadTarget.application : activeView}
              cabinetColor={bulkDownloadTarget ? bulkDownloadTarget.cabinetColor : cabinetColor}
              upperCab={bulkDownloadTarget ? (bulkDownloadTarget.upperCab || upperCab) : upperCab}
              lowerCab={bulkDownloadTarget ? (bulkDownloadTarget.lowerCab || lowerCab) : lowerCab}
              isDownloadView={true}
              selectedSamples={selectedSamples}
            />
          </div>
        </div>
        <div className="download-footer">
          <div className="footer-logo">ELEGANT FITTINGS</div>
          <div className="footer-info">
            <p>© 2026 Elegant Fittings. All rights reserved.</p>
            <p>www.elegantfittings.co.ke</p>
          </div>
        </div>
      </div>
      {/* Chat widget — all viewports; on mobile offset above CTA bar via ChatWidget.css */}
      <ChatWidget
        onToggleSampleSelectionMode={() => setIsSampleSelectionMode(prev => !prev)}
        onToggleLikedPanel={() => setShowLikedPanel(prev => !prev)}
        onCheckoutFavorites={onCheckoutFavorites}
        onOpenSamplesPage={onOpenSamplesPage}
        selectedSamples={selectedSamples}
        likedVisuals={likedVisuals}
        open={chatOpen}
        onOpenChange={setChatOpen}
      />

    </div>
  );
}

export default App;
